"use client";

import { Trans } from "@lingui/react/macro";
import { AlertCircleIcon, Loader2Icon, PackageOpenIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

export interface TableContainerProps {
  /** テーブルコンポーネント */
  children: React.ReactNode;
  /** 初回ローディング中 */
  isInitialLoading?: boolean;
  /** データ更新中 */
  isRefetching?: boolean;
  /** スケルトンコンポーネント（初回ローディング時に表示） */
  skeleton?: React.ReactNode;
  /** エラー */
  error?: Error | null;
  /** データが空か */
  isEmpty?: boolean;
  /** 空データ時のメッセージ（カスタマイズ可能） */
  emptyMessage?: React.ReactNode;
  /** コンテナーのクラス名 */
  className?: string;
}

/**
 * テーブルコンテナーコンポーネント
 *
 * @description
 * ローディング、エラー、空状態を統合管理するテーブルコンテナー。
 * 横スクロールインジケーター機能も統合。
 * SA、UA、IW等のテーブルで共通して使用できます。
 *
 * @example
 * ```tsx
 * <TableContainer
 *   isInitialLoading={isInitialLoading}
 *   isRefetching={isRefetching}
 *   skeleton={<TableSkeleton />}
 * >
 *   <DataTable data={tableData} />
 * </TableContainer>
 * ```
 */
export const TableContainer = React.memo(
  React.forwardRef<HTMLDivElement, TableContainerProps>(function TableContainer(
    {
      children,
      isInitialLoading = false,
      isRefetching = false,
      skeleton,
      error = null,
      isEmpty = false,
      emptyMessage,
      className,
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const actualRef =
      (ref as React.MutableRefObject<HTMLDivElement>) || containerRef;
    const [showShadow, setShowShadow] = useState(false);

    // 横スクロールの監視と影の制御
    useEffect(() => {
      if (isInitialLoading) return;

      const element = actualRef.current;
      if (!element) return;

      // ScrollAreaのビューポート要素を探す
      const scrollableElement = element.querySelector(
        "[data-radix-scroll-area-viewport]",
      ) as HTMLElement;
      if (!scrollableElement) return;

      const table = scrollableElement.querySelector("table");
      if (!table) return;

      let rafId: number | null = null;

      const updateShadow = () => {
        // RequestAnimationFrameでパフォーマンス最適化
        if (rafId !== null) return;

        rafId = requestAnimationFrame(() => {
          const hasOverflow =
            scrollableElement.scrollWidth > scrollableElement.clientWidth;
          const atEnd =
            scrollableElement.scrollLeft + scrollableElement.clientWidth >=
            scrollableElement.scrollWidth - 2;
          setShowShadow(hasOverflow && !atEnd);
          rafId = null;
        });
      };

      // 初回チェック
      updateShadow();

      // スクロールイベント
      scrollableElement.addEventListener("scroll", updateShadow, {
        passive: true,
      });

      // リサイズ監視
      const observer = new ResizeObserver(updateShadow);
      observer.observe(scrollableElement);
      observer.observe(table);

      return () => {
        scrollableElement.removeEventListener("scroll", updateShadow);
        observer.disconnect();
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    }, [isInitialLoading, actualRef]);

    // 初回ローディング中はスケルトンを表示
    if (isInitialLoading && skeleton) {
      return <>{skeleton}</>;
    }

    // エラーと空状態の表示判定
    const showError = !isInitialLoading && !!error;
    const showEmpty = !isInitialLoading && !error && isEmpty;

    return (
      <div className="relative">
        {/* スクロールコンテナ */}
        <div
          ref={actualRef}
          className={cn("relative", className)}
          aria-busy={isRefetching}
        >
          {showError && <TableErrorState message={error?.message} />}
          {showEmpty && <TableEmptyState message={emptyMessage} />}
          <ScrollArea
            className={cn(skeleton ? "min-h-[400px]" : "min-h-[200px]")}
          >
            <div
              // sticky カラム用に ScrollArea 直下の overflow を可視化
              className="[&>.overflow-x-auto]:overflow-visible [&>.overflow-x-auto>.overflow-auto]:overflow-visible"
            >
              {children}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          {skeleton && <TableLoadingOverlay isLoading={isRefetching} />}
        </div>

        {/* 右端の影 - z-index: 1 でコンテンツの上に配置、エラーや空状態の時は非表示 */}
        {showShadow && !showError && !showEmpty && (
          <div
            data-scroll-shadow="true"
            className="absolute top-0 right-0 bottom-0 w-4 pointer-events-none z-[1] bg-gradient-to-l from-black/8 via-black/4 to-transparent dark:from-white/8 dark:via-white/4 dark:to-transparent"
            aria-hidden="true"
          />
        )}
      </div>
    );
  }),
);

TableContainer.displayName = "TableContainer";

/**
 * テーブルの空状態表示コンポーネント
 *
 * @description
 * データがない場合に表示する共通の空状態コンポーネント。
 * SA、UA、IW、その他のテーブルで統一的に使用できます。
 */
interface TableEmptyStateProps {
  /** カスタムクラス名 */
  className?: string;
  /** カスタムメッセージ */
  message?: React.ReactNode;
}

const TableEmptyState = React.memo<TableEmptyStateProps>(
  function TableEmptyState({ className, message }) {
    return (
      <div
        className={cn(
          "absolute inset-0 pt-40 flex items-start justify-center bg-background/80 z-1",
          className,
        )}
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center justify-center gap-4 bg-background p-8 border">
          <p className="flex items-center gap-2 font-semibold text-xl">
            <PackageOpenIcon
              className="h-6 w-6 text-muted-foreground"
              aria-hidden="true"
            />
            <Trans>No data found</Trans>
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            {message || (
              <Trans>Change your filter conditions and try again.</Trans>
            )}
          </p>
        </div>
      </div>
    );
  },
);

TableEmptyState.displayName = "TableEmptyState";

/**
 * テーブルのエラー状態表示コンポーネント
 *
 * @description
 * エラーが発生した場合に表示する共通のエラー状態コンポーネント。
 * SA、UA、IW、その他のテーブルで統一的に使用できます。
 */
interface TableErrorStateProps {
  /** カスタムクラス名 */
  className?: string;
  /** エラーメッセージ */
  message?: string;
}

const TableErrorState = React.memo<TableErrorStateProps>(
  function TableErrorState({ className, message }) {
    return (
      <div
        className={cn(
          "absolute inset-0 pt-40 flex items-start justify-center bg-background/80 z-1",
          className,
        )}
        role="alert"
        aria-live="assertive"
      >
        <div className="flex flex-col items-center justify-center gap-4 bg-background p-4 border">
          <p className="flex items-center gap-2 font-semibold text-xl">
            <AlertCircleIcon
              className="h-6 w-6 text-destructive"
              aria-hidden="true"
            />
            <Trans>Error</Trans>
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            {message || (
              <Trans>Something went wrong while loading the data.</Trans>
            )}
          </p>
        </div>
      </div>
    );
  },
);

TableErrorState.displayName = "TableErrorState";

/**
 * テーブル用の汎用ローディングオーバーレイコンポーネント
 *
 * @description
 * SA、UA、IW、その他のテーブルで共通して使用できるローディングオーバーレイ。
 * パフォーマンス最適化のためReact.memoでメモ化されています。
 */
interface TableLoadingOverlayProps {
  /** ローディング表示の有無 */
  isLoading: boolean;
  /** カスタムクラス名 */
  className?: string;
}

const TableLoadingOverlay = React.memo<TableLoadingOverlayProps>(
  function TableLoadingOverlay({ isLoading, className }) {
    // ローディング中でない場合は何も表示しない
    if (!isLoading) {
      return null;
    }

    return (
      <div
        className={cn(
          "absolute inset-0 pointer-events-none transition-all duration-300 flex justify-center items-start bg-background/30 pt-12",
          className,
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="flex items-center gap-2 bg-background/80 backdrop-blur-sm px-4 py-2 rounded-md shadow-md border border-border/50 h-fit">
          <Loader2Icon
            className="animate-spin text-primary/80 h-5 w-5"
            aria-hidden="true"
          />
          <span className="text-xs font-medium text-muted-foreground">
            <Trans>Fetching data...</Trans>
          </span>
        </div>
      </div>
    );
  },
);

TableLoadingOverlay.displayName = "TableLoadingOverlay";
